import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectItem } from "@heroui/react";

import { sortPeriods } from "@/features/S2Dashboard/model/utils";
import { r1Api } from "@/features/R1Dashboard/services/api";
import WorldMapChoropleth, {
  type WorldMapItem,
} from "@/features/R1Dashboard/components/WorldMapChoropleth";
import BreakdownPieChart from "@/features/R1Dashboard/components/BreakdownPieChart";
import BreakdownBarChart from "@/features/R1Dashboard/components/BreakdownBarChart";

type MapMode = "funds" | "positions";

const MODE_OPTIONS: { key: MapMode; label: string }[] = [
  { key: "funds", label: "Funds Map" },
  // Counts positions, not unique companies — a company held by 3 funds
  // counts 3 times. Named accordingly to avoid confusion with the
  // deduplicated "Portfolio Companies Map" on the Fund Level page.
  { key: "positions", label: "Portfolio Positions Map" },
];

export default function R1LevelAnalyticsView() {
  const [mode, setMode] = useState<MapMode>("funds");

  const {
    data: breakdowns,
    isLoading: breakdownsLoading,
    isError: breakdownsError,
  } = useQuery({
    queryKey: ["r1", "funds-breakdown"],
    queryFn: () => r1Api.getFundsBreakdown(),
    staleTime: 60_000,
  });

  const {
    data: fundsGeography,
    isLoading: fundsGeographyLoading,
    isError: fundsGeographyError,
  } = useQuery({
    queryKey: ["r1", "funds-geography"],
    queryFn: () => r1Api.getFundsGeography(),
    staleTime: 60_000,
    enabled: mode === "funds",
  });

  const { data: periodsPerFund } = useQuery({
    queryKey: ["r1", "reporting-periods-per-fund", "all"],
    queryFn: () => r1Api.getReportingPeriodsPerFund({ mode: "all" }),
    staleTime: 60_000,
    enabled: mode === "positions",
  });

  const allPeriods = useMemo(() => {
    const set = new Set<string>();
    for (const periods of Object.values(periodsPerFund ?? {})) {
      for (const p of periods) set.add(p);
    }
    return sortPeriods(Array.from(set)).reverse();
  }, [periodsPerFund]);

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  useEffect(() => {
    if (allPeriods.length === 0) {
      setSelectedPeriod(null);
      return;
    }
    if (!selectedPeriod || !allPeriods.includes(selectedPeriod)) {
      setSelectedPeriod(allPeriods[0]);
    }
  }, [allPeriods, selectedPeriod]);

  const {
    data: positionsGeography,
    isLoading: positionsGeographyLoading,
    isError: positionsGeographyError,
  } = useQuery({
    queryKey: ["r1", "positions-geography", selectedPeriod],
    queryFn: () => r1Api.getPositionsGeography(selectedPeriod!),
    staleTime: 60_000,
    enabled: mode === "positions" && !!selectedPeriod,
  });

  const fundsItems = useMemo<WorldMapItem[]>(() => {
    return (fundsGeography ?? []).map((fund) => ({
      name: fund.fund_name,
      countryIsoNumeric: fund.country_iso_numeric,
    }));
  }, [fundsGeography]);

  const positionsItems = useMemo<WorldMapItem[]>(() => {
    return (positionsGeography ?? []).map((position) => ({
      name: position.company_name,
      countryIsoNumeric: position.country_iso_numeric,
    }));
  }, [positionsGeography]);

  const modeSelector = (
    <Select
      size="sm"
      selectedKeys={new Set([mode])}
      onSelectionChange={(keys) => {
        if (keys === "all") return;
        const val = Array.from(keys)[0] as MapMode;
        if (val) setMode(val);
      }}
      classNames={{
        trigger: "h-6 min-h-6 bg-gray-50 shadow-none text-xs w-52",
        value: "text-xs",
      }}
      aria-label="Map type"
    >
      {MODE_OPTIONS.map((opt) => (
        <SelectItem key={opt.key}>{opt.label}</SelectItem>
      ))}
    </Select>
  );

  const periodSelector = mode === "positions" && allPeriods.length > 0 && (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
        Quarter
      </span>
      <Select
        size="sm"
        selectedKeys={selectedPeriod ? new Set([selectedPeriod]) : new Set()}
        onSelectionChange={(keys) => {
          if (keys === "all") return;
          const val = Array.from(keys)[0] as string;
          if (val) setSelectedPeriod(val);
        }}
        classNames={{
          trigger: "h-6 min-h-6 bg-gray-50 shadow-none text-xs w-32",
          value: "text-xs",
        }}
        aria-label="Quarter for portfolio companies map"
      >
        {allPeriods.map((p) => (
          <SelectItem key={p}>{p}</SelectItem>
        ))}
      </Select>
    </div>
  );

  return (
    <div className="flex flex-col gap-10 bg-white">
      <section>
        <h2 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
          Fund Breakdown
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          <BreakdownPieChart
            title="Strategy"
            data={breakdowns?.strategy.breakdown ?? []}
            fillRate={breakdowns?.strategy.fill_rate ?? 0}
            paletteOffset={0}
            isLoading={breakdownsLoading}
            isError={breakdownsError}
          />
          <BreakdownPieChart
            title="Sector"
            data={breakdowns?.sector.breakdown ?? []}
            fillRate={breakdowns?.sector.fill_rate ?? 0}
            paletteOffset={3}
            isLoading={breakdownsLoading}
            isError={breakdownsError}
          />
          <BreakdownBarChart
            title="Vintage"
            data={breakdowns?.vintage.breakdown ?? []}
            fillRate={breakdowns?.vintage.fill_rate ?? 0}
            isLoading={breakdownsLoading}
            isError={breakdownsError}
          />
          <BreakdownPieChart
            title="Country"
            data={breakdowns?.country.breakdown ?? []}
            fillRate={breakdowns?.country.fill_rate ?? 0}
            paletteOffset={9}
            isLoading={breakdownsLoading}
            isError={breakdownsError}
          />
          <BreakdownPieChart
            title="Currency"
            data={breakdowns?.currency.breakdown ?? []}
            fillRate={breakdowns?.currency.fill_rate ?? 0}
            paletteOffset={12}
            isLoading={breakdownsLoading}
            isError={breakdownsError}
          />
        </div>
      </section>

      {mode === "funds" ? (
        <WorldMapChoropleth
          title="Map"
          items={fundsItems}
          unitSingular="fund"
          unitPlural="funds"
          headerExtra={modeSelector}
          isLoading={fundsGeographyLoading}
          isError={fundsGeographyError}
        />
      ) : (
        <WorldMapChoropleth
          title="Map"
          items={positionsItems}
          unitSingular="position"
          unitPlural="positions"
          headerExtra={
            <div className="flex items-center gap-4">
              {modeSelector}
              {periodSelector}
            </div>
          }
          isLoading={positionsGeographyLoading}
          isError={positionsGeographyError}
        />
      )}
    </div>
  );
}
